import { useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Screen } from '../components/Screen'
import { Field, useLeaveGuard } from '../components/Form'
import { LimitNote } from '../components/LimitNote'
import { EmptyState, ErrorState, SaveFailedAlert, Sheet, Skeleton } from '../components/States'
import { useEnv } from '../context'
import { notesKey, useAthlete, useGroup, useNotes, useNotesTrash, useSingleFlight } from '../data/hooks'
import { NOTE_KINDS, noteBody, noteKindLabel, titleFrom, type NoteOwner, type NoteOwnerKind } from '../data/notes'
import { formatDayShort, parseIsoDate } from '../data/schedule'
import { SliceApiError, type Note, type NoteInput } from '../data/types'
import { useBackTo } from './AthleteScreen'

/**
 * Записи о спортсмене и группе (этап 5 шаг 3, SC17). Одни правила для обоих:
 * быстрая запись прямо на вкладке, правка и удаление - отдельным экраном.
 * Права решает сервер (решение владельца 2026-10-06): читают все тренеры
 * клуба, править и удалять - автор записи и владелец клуба, запись без
 * автора - только владелец. Кнопки правки - только у того, кому можно
 * (`can_edit`), остальным - «Автор: ...». Удаление - в корзину на 30 дней
 * (`TrashScreens.tsx`), после подтверждения с названием записи.
 */

/** Адрес вкладки записей: туда возвращаются после правки по прямой ссылке. */
export function notesTabPath(owner: NoteOwner): string {
  return owner.kind === 'athlete' ? `/athletes/${owner.id}?tab=notes` : `/groups/${owner.id}?tab=notes`
}

/** Корзина записей спортсмена или группы. */
export function notesTrashPath(owner: NoteOwner): string {
  return owner.kind === 'athlete' ? `/athletes/${owner.id}/notes/trash` : `/groups/${owner.id}/notes/trash`
}

export function noteEditPath(owner: NoteOwner, noteId: string): string {
  return owner.kind === 'athlete' ? `/athletes/${owner.id}/notes/${noteId}` : `/groups/${owner.id}/notes/${noteId}`
}

const noteApi = {
  add: (api: ReturnType<typeof useEnv>['api'], owner: NoteOwner, input: NoteInput) =>
    owner.kind === 'athlete' ? api.addAthleteNote(owner.id, input) : api.addGroupNote(owner.id, input),
  update: (api: ReturnType<typeof useEnv>['api'], owner: NoteOwner, noteId: string, input: NoteInput) =>
    owner.kind === 'athlete' ? api.updateAthleteNote(owner.id, noteId, input) : api.updateGroupNote(owner.id, noteId, input),
  remove: (api: ReturnType<typeof useEnv>['api'], owner: NoteOwner, noteId: string) =>
    owner.kind === 'athlete' ? api.deleteAthleteNote(owner.id, noteId) : api.deleteGroupNote(owner.id, noteId),
}

const isKind = (e: unknown, kind: SliceApiError['kind']) => e instanceof SliceApiError && e.kind === kind

/** Подписка клуба закончилась (423): правило, а не сбой. */
const isLocked = (e: unknown) => isKind(e, 'limit')

export const SCOPE_HINT = 'Записи видят все тренеры клуба. Править и удалять запись может её автор и владелец клуба.'

/** Чья запись: имя автора или «не сохранён» у старых записей. */
export function authorLine(n: Pick<Note, 'author_name'>): string {
  return n.author_name ? `Автор: ${n.author_name}` : 'Автор не сохранён'
}

/** Почему править нельзя: у записи без автора правит только владелец. */
function readOnlyReason(n: Pick<Note, 'author_name'>): string {
  return n.author_name
    ? 'Править и удалять запись может её автор и владелец клуба.'
    : 'Автор записи не сохранён: править и удалять её может только владелец клуба.'
}

function KindChips({
  owner,
  value,
  onChange,
  labelId,
}: {
  owner: NoteOwnerKind
  value: string
  onChange: (v: string) => void
  labelId: string
}) {
  return (
    <div className="pc-chips pc-chips--tall" role="group" aria-labelledby={labelId} style={{ flexWrap: 'wrap', marginTop: 0 }}>
      {NOTE_KINDS[owner].map(([k, label]) => (
        <button key={k} type="button" aria-pressed={value === k} onClick={() => onChange(k)}>
          {label}
        </button>
      ))}
    </div>
  )
}

export function noteDate(n: Pick<Note, 'created_at' | 'updated_at'>): string | null {
  const created = parseIsoDate(n.created_at?.slice(0, 10))
  if (!created) return null
  const edited = Date.parse(n.updated_at) - Date.parse(n.created_at) > 60_000 ? parseIsoDate(n.updated_at.slice(0, 10)) : null
  if (!edited) return formatDayShort(created)
  // Изменено в тот же день - без второй одинаковой даты
  return edited.getTime() === created.getTime()
    ? `${formatDayShort(created)}, изменено`
    : `${formatDayShort(created)}, изменено ${formatDayShort(edited)}`
}

/** Вкладка «Записи» карточки спортсмена и экрана группы. */
export function NotesSection({ owner }: { owner: NoteOwner }) {
  const { api, toast } = useEnv()
  const navigate = useNavigate()
  const client = useQueryClient()
  const notes = useNotes(owner)
  const flight = useSingleFlight()
  const [text, setText] = useState('')
  const [kind, setKind] = useState('general')
  const [error, setError] = useState<'locked' | 'failed' | null>(null)

  const add = async () => {
    const content = text.trim()
    if (!content) return
    setError(null)
    try {
      const res = await flight.run(() => noteApi.add(api, owner, { title: titleFrom(content), content, noteType: kind }))
      if (!res) return
      setText('')
      setKind('general')
      await client.invalidateQueries({ queryKey: notesKey(owner) })
      toast('Запись сохранена')
    } catch (err) {
      setError(isLocked(err) ? 'locked' : 'failed')
      // Ответ мог потеряться после записи: список покажет, если она всё же есть.
      if (isKind(err, 'network')) void client.invalidateQueries({ queryKey: notesKey(owner) })
    }
  }

  return (
    <section style={{ marginTop: 16 }}>
      <div className="pc-field">
        <label htmlFor={`pc-note-${owner.kind}`}>Новая запись</label>
        <textarea
          id={`pc-note-${owner.kind}`}
          className="pc-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            owner.kind === 'athlete'
              ? 'Например: после травмы колена - без прыжков до конца месяца'
              : 'Например: к аттестации повторить стойки и первое ката'
          }
        />
        <span className="pc-field__label" id={`pc-note-kind-${owner.kind}`}>
          Тип записи
        </span>
        <KindChips owner={owner.kind} value={kind} onChange={setKind} labelId={`pc-note-kind-${owner.kind}`} />
        {error === 'locked' ? (
          <LimitNote what="записей" reason="locked" />
        ) : error ? (
          <SaveFailedAlert>Текст остался в поле, попробуйте ещё раз.</SaveFailedAlert>
        ) : null}
        <button type="button" className="pc-btn" disabled={flight.busy || !text.trim()} onClick={() => void add()}>
          {flight.busy ? 'Сохраняю…' : 'Сохранить запись'}
        </button>
        <div className="pc-field__hint">{SCOPE_HINT}</div>
      </div>

      {notes.isPending ? (
        <Skeleton rows={2} label="Загружаю записи" />
      ) : notes.isError ? (
        <ErrorState title="Не удалось загрузить записи" onRetry={() => void notes.refetch()} />
      ) : notes.data.length === 0 ? (
        <EmptyState
          title="Записей пока нет"
          text={
            owner.kind === 'athlete'
              ? 'Здесь хранятся наблюдения тренера о спортсмене.'
              : 'Здесь хранятся планы, наблюдения и итоги по группе.'
          }
        />
      ) : (
        <ul className="pc-list" style={{ marginTop: 12 }} aria-label="Записи">
          {notes.data.map((n) => {
            const body = noteBody(n)
            const date = noteDate(n)
            return (
              <li key={n.id} className="pc-note-item">
                <div className="pc-note-item__main">
                  {n.note_type !== 'general' && <span className="pc-badge pc-badge--neutral">{noteKindLabel(n.note_type)}</span>}
                  <div className="pc-kv__label">{n.title}</div>
                  {body && (
                    <div className="pc-kv__value" style={{ whiteSpace: 'pre-wrap' }}>
                      {body}
                    </div>
                  )}
                  {date && <div className="pc-field__hint">{date}</div>}
                  <div className="pc-field__hint">{authorLine(n)}</div>
                </div>
                {n.can_edit && (
                  <button
                    type="button"
                    className="pc-btn"
                    aria-label={`Изменить запись «${n.title}»`}
                    onClick={() => navigate(noteEditPath(owner, n.id))}
                  >
                    Изменить
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <TrashLink owner={owner} />
    </section>
  )
}

/**
 * «Удалённые (N)» внизу вкладки: корзина там же, где удаляли, - вернуть
 * запись можно, не уходя из карточки. Пустая корзина - ссылки нет; не
 * загрузилась (нет сети) - ссылка без числа, экран корзины объяснит.
 */
function TrashLink({ owner }: { owner: NoteOwner }) {
  const navigate = useNavigate()
  const bin = useNotesTrash(owner)
  if (bin.isPending || (bin.isSuccess && bin.data.length === 0)) return null
  return (
    <button type="button" className="pc-btn pc-btn--block" style={{ marginTop: 12 }} onClick={() => navigate(notesTrashPath(owner))}>
      {bin.isSuccess ? `Удалённые (${bin.data.length})` : 'Удалённые'}
    </button>
  )
}

/**
 * Правка и удаление записи: `/athletes/:id/notes/:noteId`,
 * `/groups/:id/notes/:noteId`. Запись берётся из списка владельца - отдельной
 * ручки чтения у экрана нет, а список уже есть в снимке без сети.
 */
export function AthleteNoteEditScreen() {
  const { id = '' } = useParams()
  const athlete = useAthlete(id)
  return <NoteEditScreen kind="athlete" id={id} ownerName={athlete.data?.name} />
}

export function GroupNoteEditScreen() {
  const { id = '' } = useParams()
  const group = useGroup(id)
  return <NoteEditScreen kind="group" id={id} ownerName={group.data?.name} />
}

function NoteEditScreen({ kind, id, ownerName }: { kind: NoteOwnerKind; id: string; ownerName?: string }) {
  const { noteId = '' } = useParams()
  const owner: NoteOwner = { kind, id }
  const notes = useNotes(owner)
  const back = useBackTo(notesTabPath(owner))

  if (notes.isPending) {
    return (
      <Screen title="Запись" subtitle={ownerName} back={back} hideNav>
        <Skeleton rows={3} label="Загружаю запись" />
      </Screen>
    )
  }
  if (notes.isError) {
    const gone = (notes.error as { kind?: string })?.kind === 'not_found'
    return (
      <Screen title="Запись" subtitle={ownerName} back={back} hideNav>
        {gone ? (
          <EmptyState title={kind === 'athlete' ? 'Спортсмен не найден' : 'Группа не найдена'} text="Возможно, её удалили или ссылка устарела." />
        ) : (
          <ErrorState title="Не удалось открыть запись" onRetry={() => void notes.refetch()} />
        )}
      </Screen>
    )
  }
  const note = notes.data.find((n) => n.id === noteId)
  if (!note) {
    return (
      <Screen title="Запись" subtitle={ownerName} back={back} hideNav>
        <EmptyState
          title="Запись не найдена"
          text="Возможно, её удалили: удалённые записи 30 дней лежат в корзине на вкладке «Записи»."
          action={
            <button type="button" className="pc-btn pc-btn--primary" onClick={back}>
              К записям
            </button>
          }
        />
      </Screen>
    )
  }
  if (!note.can_edit) return <NoteView owner={owner} note={note} ownerName={ownerName} />
  return <NoteForm key={note.id} owner={owner} note={note} ownerName={ownerName} />
}

/** Чужая запись по прямой ссылке: только просмотр и кто может править. */
function NoteView({ owner, note, ownerName }: { owner: NoteOwner; note: Note; ownerName?: string }) {
  const back = useBackTo(notesTabPath(owner))
  const body = noteBody(note)
  const date = noteDate(note)
  return (
    <Screen title="Запись" subtitle={ownerName} back={back} hideNav>
      <div className="pc-card" style={{ marginTop: 12 }}>
        {note.note_type !== 'general' && <span className="pc-badge pc-badge--neutral">{noteKindLabel(note.note_type)}</span>}
        <h2 className="pc-section-title" style={{ margin: '4px 0' }}>
          {note.title}
        </h2>
        {body && <p style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{body}</p>}
        {date && <div className="pc-field__hint">{date}</div>}
        <div className="pc-field__hint">{authorLine(note)}</div>
      </div>
      <p className="pc-note" style={{ marginTop: 12 }}>
        {readOnlyReason(note)}
      </p>
    </Screen>
  )
}

function NoteForm({ owner, note, ownerName }: { owner: NoteOwner; note: Note; ownerName?: string }) {
  const { api, toast } = useEnv()
  const client = useQueryClient()
  const back0 = useBackTo(notesTabPath(owner))
  const saveFlight = useSingleFlight()
  const deleteFlight = useSingleFlight()
  // Заголовок, собранный из первой строки, остаётся «автоматическим»: поле
  // пустое, и при правке первой строки заголовок меняется вместе с ней.
  const autoTitle = note.title === titleFrom(note.content)
  const [title, setTitle] = useState(autoTitle ? '' : note.title)
  const [content, setContent] = useState(note.content)
  const [kind, setKind] = useState(note.note_type)
  const [contentError, setContentError] = useState<string | undefined>()
  const [saveError, setSaveError] = useState<'locked' | 'gone' | 'forbidden' | 'failed' | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [deleteError, setDeleteError] = useState<'locked' | 'forbidden' | 'failed' | null>(null)
  const [done, setDone] = useState(false)
  // Сохранено или удалено: экран уходит, второе нажатие до ухода ничего не делает.
  const finished = useRef(false)

  const dirty = !done && (title !== (autoTitle ? '' : note.title) || content !== note.content || kind !== note.note_type)
  const leave = useLeaveGuard(dirty && !saveFlight.busy)
  const back = () => leave.guard(back0)

  const input = (): NoteInput | null => {
    const text = content.trim()
    if (!text) {
      setContentError('Напишите текст записи')
      return null
    }
    setContentError(undefined)
    return { title: title.trim() || titleFrom(text), content: text, noteType: kind }
  }

  const finish = async (message: string) => {
    finished.current = true
    setDone(true)
    await client.invalidateQueries({ queryKey: notesKey(owner) })
    toast(message)
    back0()
  }

  const save = async () => {
    if (finished.current) return
    const value = input()
    if (!value) return
    setSaveError(null)
    try {
      const res = await saveFlight.run(() => noteApi.update(api, owner, note.id, value))
      if (!res) return
      await finish('Запись сохранена')
    } catch (err) {
      setSaveError(isKind(err, 'not_found') ? 'gone' : isKind(err, 'forbidden') ? 'forbidden' : isLocked(err) ? 'locked' : 'failed')
    }
  }

  // Запись удалили, пока шла правка: текст не теряем - сохраняем новой.
  const saveAsNew = async () => {
    if (finished.current) return
    const value = input()
    if (!value) return
    setSaveError(null)
    try {
      const res = await saveFlight.run(() => noteApi.add(api, owner, value))
      if (!res) return
      await finish('Сохранено новой записью')
    } catch (err) {
      setSaveError(isLocked(err) ? 'locked' : 'failed')
    }
  }

  const remove = async () => {
    if (finished.current) return
    setDeleteError(null)
    try {
      const res = await deleteFlight.run(() => noteApi.remove(api, owner, note.id))
      if (res === null) return
      setConfirmDelete(false)
      await finish('Запись удалена: 30 дней её можно вернуть')
    } catch (err) {
      // Повтор после потерянного ответа: записи уже нет - это и было нужно.
      if (isKind(err, 'not_found')) {
        setConfirmDelete(false)
        await finish('Запись уже удалена')
        return
      }
      setDeleteError(isKind(err, 'forbidden') ? 'forbidden' : isLocked(err) ? 'locked' : 'failed')
    }
  }

  return (
    <Screen
      title="Запись"
      subtitle={ownerName}
      back={back}
      hideNav
      actionBar={
        <button
          type="button"
          className="pc-btn pc-btn--primary pc-btn--block"
          disabled={saveFlight.busy || saveError === 'gone'}
          onClick={() => void save()}
        >
          {saveFlight.busy ? 'Сохраняю…' : 'Сохранить'}
        </button>
      }
    >
      <form
        className="pc-form"
        noValidate
        onSubmit={(ev) => {
          ev.preventDefault()
          void save()
        }}
      >
        {saveError === 'gone' ? (
          <div className="pc-alert" role="alert">
            Запись удалили, пока шла правка: изменения не сохранены. Текст остался на экране.
            <button
              type="button"
              className="pc-btn pc-btn--block"
              style={{ marginTop: 8 }}
              disabled={saveFlight.busy}
              onClick={() => void saveAsNew()}
            >
              {saveFlight.busy ? 'Сохраняю…' : 'Сохранить новой записью'}
            </button>
          </div>
        ) : saveError === 'locked' ? (
          <LockedNote guard={leave.guard} />
        ) : saveError === 'forbidden' ? (
          <div className="pc-alert" role="alert">
            {readOnlyReason(note)} Изменения не сохранены, текст остался на экране.
          </div>
        ) : saveError ? (
          <SaveFailedAlert>Изменения остались на экране, попробуйте ещё раз.</SaveFailedAlert>
        ) : null}
        <div className="pc-field">
          <span className="pc-field__label" id="pc-note-edit-kind">
            Тип записи
          </span>
          <KindChips owner={owner.kind} value={kind} onChange={setKind} labelId="pc-note-edit-kind" />
        </div>
        <Field label="Заголовок" hint="Пусто - первая строка текста">
          {(p) => (
            <input
              {...p}
              className="pc-input"
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={titleFrom(content) || 'Первая строка текста'}
              autoComplete="off"
            />
          )}
        </Field>
        <Field label="Текст" error={contentError}>
          {(p) => <textarea {...p} className="pc-input" rows={6} value={content} onChange={(e) => setContent(e.target.value)} />}
        </Field>
        <p className="pc-muted" style={{ margin: 0, fontSize: 14 }}>
          {authorLine(note)}. {SCOPE_HINT}
        </p>
      </form>
      <button
        type="button"
        className="pc-btn pc-btn--block pc-btn--danger"
        style={{ marginTop: 24 }}
        onClick={() => {
          setDeleteError(null)
          setConfirmDelete(true)
        }}
      >
        Удалить запись
      </button>

      {confirmDelete && (
        <Sheet title="Удалить запись?" onClose={() => setConfirmDelete(false)}>
          <p className="pc-muted" style={{ margin: '0 0 12px', fontSize: 14 }}>
            «{note.title}» попадёт в корзину на 30 дней: вернуть её можно на вкладке «Записи», «Удалённые». Потом запись
            удалится насовсем.
          </p>
          {deleteError === 'locked' ? (
            <LockedNote />
          ) : deleteError === 'forbidden' ? (
            <div className="pc-alert" role="alert">
              {readOnlyReason(note)} Ничего не удалено.
            </div>
          ) : deleteError ? (
            <SaveFailedAlert>Повторите: если запись уже удалена, повтор это покажет.</SaveFailedAlert>
          ) : null}
          <button
            type="button"
            className="pc-btn pc-btn--block pc-btn--danger"
            style={{ marginTop: 12 }}
            disabled={deleteFlight.busy}
            onClick={() => void remove()}
          >
            {deleteFlight.busy ? 'Удаляю…' : 'Удалить запись'}
          </button>
          <button type="button" className="pc-btn pc-btn--block" onClick={() => setConfirmDelete(false)}>
            Отмена
          </button>
        </Sheet>
      )}
      {leave.dialog}
    </Screen>
  )
}

/**
 * Подписка закончилась: правка и удаление не проходят, клуб только для чтения.
 * guard - охрана ухода с формы: несохранённая правка не теряется молча.
 */
export function LockedNote({ guard }: { guard?: (leave: () => void) => void }) {
  const navigate = useNavigate()
  const open = () => navigate('/club/subscription')
  return (
    <div className="pc-note" role="alert">
      Подписка клуба закончилась: клуб сейчас только для чтения. Ничего не изменилось.
      <button
        type="button"
        className="pc-btn pc-btn--block"
        style={{ marginTop: 8 }}
        onClick={() => (guard ? guard(open) : open())}
      >
        Открыть подписку клуба
      </button>
    </div>
  )
}
